import React, { useState, useRef, useEffect, useCallback } from "react";
import SlidingQuestion from "../components/question/SlidingQuestion";
import LocationDropdown from "../components/question/LocationDropdown";
import LinearProgressWithLabel from "../components/question/linearProgress";
import APIHelper from "../APIHelper.js";

import { Link } from "react-router-dom";
import { Button, Typography, Box } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import { questions } from "../constants/questions.js";
import { us_state_to_abbrev } from "../constants/state_code";
import resources from "../constants/resource.json";
import { typeTransform } from "../constants/resourceType";

let userResponses = [{ question: 0, answer: "US" }];

const lastQuestionObject = {
  question:
    "The next questions deal with homelessness, LGBTQ+, disabilities and low income, do you identify with any of these communities?",
  responseChoices: ["Yes", "No"],
};

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const Questionaire = () => {
  const [index, setIndex] = useState(1);
  const [locationState, setLocationState] = useState("");
  const [lastQuestionAsked, setLastQuestionAsked] = useState(false);

  const questionsRef = useRef(questions);
  const resourcesRef = useRef(resources);
  const outputRef = useRef([]);
  const indexRef = useRef(1);
  const locationStateRef = useRef("");

  // Keep refs in sync with state for use in synchronous logic
  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  useEffect(() => {
    locationStateRef.current = locationState;
  }, [locationState]);

  // Cleanup: send responses to API on unmount
  useEffect(() => {
    return () => {
      APIHelper.addResponses(userResponses);
    };
  }, []);

  const outputHandler = useCallback((idx, resourceTypes) => {
    const obj = {
      index: idx,
      resources: [],
    };
    for (let i = 0; i < resourceTypes.length; i++) {
      const currType = resourceTypes[i];
      const res =
        resourcesRef.current["resourceType"][currType]["resources"];
      for (let j = 0; j < res.length; j++) {
        res[j]["resourceTypeName"] = typeTransform[resourceTypes[i]];
      }
      obj.resources.push(...res);
    }
    if (obj.resources.length > 0) {
      outputRef.current.push(obj);
    }
  }, []);

  // Control the question flow
  const questionManager = useCallback(() => {
    const currentIndex = indexRef.current;
    const { usersResponse } = questionsRef.current[currentIndex];
    let under18 = false;
    let question3 = false;

    switch (currentIndex) {
      default:
        console.log("Oh no! No case found");
        break;

      case 0:
        // Save the users location
        setIndex(currentIndex + 1);
        outputHandler(currentIndex, []);
        break;
      case 1:
        // Save the users location
        setIndex(currentIndex + 1);
        const state_code = us_state_to_abbrev[locationStateRef.current];
        resourcesRef.current = resources[state_code];
        outputHandler(currentIndex, []);
        break;
      case 2:
        if (usersResponse === 0) {
          // SA, Proceed to Question 2B
          outputHandler(currentIndex, ["sa"]);
          setIndex(currentIndex + 1);
        } else if (usersResponse === 1) {
          // DV, recommend resources, then proceed to Q3
          outputHandler(currentIndex, [
            "domesticViolence",
            "legalAssistance",
          ]);
          setIndex(currentIndex + 2);
        } else if (usersResponse === 2) {
          // Both; show resources and then question 2B
          outputHandler(currentIndex, [
            "sa",
            "domesticViolence",
            "legalAssistance",
          ]);
          setIndex(currentIndex + 1);
        } else {
          // None of the above, proceed to question 3
          outputHandler(currentIndex, []);
          setIndex(currentIndex + 2);
        }
        break;

      case 3:
        // Question 2B
        if (usersResponse === 0) {
          // yes
          outputHandler(currentIndex, ["hivAids"]);
        } else {
          outputHandler(currentIndex, []);
        }
        // Proceed to Q3
        setIndex(currentIndex + 1);
        break;

      case 4:
        // Question 3
        if (usersResponse === 0) {
          // Resources and then Q3B
          question3 = true;
          outputHandler(currentIndex, ["leaAndCourtAdvocateInfo"]);
          setIndex(currentIndex + 1);
        } else {
          // Proceed to Q4
          outputHandler(currentIndex, []);
          setIndex(currentIndex + 2);
        }
        break;

      case 5:
        // Question 3B
        if (usersResponse === 0) {
          // Resources, then Q4
          outputHandler(currentIndex, ["vine"]);
        } else {
          outputHandler(currentIndex, []);
        }
        setIndex(currentIndex + 1);
        break;

      case 6:
        // Question 4
        if (usersResponse === 0) {
          // Q5
          setIndex(currentIndex + 1);
        } else {
          // Q6
          setIndex(currentIndex + 3);
        }
        outputHandler(currentIndex, []);
        break;

      case 7:
        // Question 5
        if (usersResponse === 0) {
          // Q5b
          setIndex(currentIndex + 1);
        } else {
          // Q6
          setIndex(currentIndex + 2);
        }
        outputHandler(currentIndex, []);
        break;

      case 8:
        // Question 5B
        if (usersResponse === 0) {
          // Resources, then Q6
          outputHandler(currentIndex, ["dcyf"]);
        } else {
          outputHandler(currentIndex, []);
        }
        setIndex(currentIndex + 1);
        break;

      case 9:
        // Question 6
        if (usersResponse === 0) {
          // Q6B
          setIndex(currentIndex + 1);
        } else {
          // Q7
          setIndex(currentIndex + 3);
        }
        outputHandler(currentIndex, []);
        break;

      case 10:
        // Question 6B
        if (usersResponse === 0) {
          // Resources, then Q7
          outputHandler(currentIndex, ["cvcp"]);
          setIndex(currentIndex + 2);
        } else {
          // Q6C
          outputHandler(currentIndex, []);
          setIndex(currentIndex + 1);
        }
        break;

      case 11:
        // Question 6C
        if (usersResponse === 0) {
          // Resources, then Q7
          outputHandler(currentIndex, ["cvcp"]);
        } else {
          outputHandler(currentIndex, []);
        }
        setIndex(currentIndex + 1);
        break;

      case 12:
        // Question 7
        if (usersResponse === 0) {
          // The user is under 18
          under18 = true;
        }
        outputHandler(currentIndex, []);
        setIndex(currentIndex + 1);
        break;

      case 13:
        // Question 8
        if (usersResponse === 0) {
          outputHandler(currentIndex, ["clinical"]);
          if (under18) {
            // placeholder for under18-specific logic
          }
          if (question3) {
            // placeholder for question3-specific logic
          }
        } else {
          outputHandler(currentIndex, []);
        }
        setIndex(currentIndex + 2);
        break;

      case 14:
        // Question 9
        if (usersResponse === 0) {
          // clinical/advocacy services
        }
        outputHandler(currentIndex, []);
        setIndex(currentIndex + 1);
        break;

      case 15:
        // Question 10
        if (usersResponse === 0) {
          // Resources, then Q10B
          outputHandler(currentIndex, []);
          setIndex(currentIndex + 1);
        } else {
          // Q11
          outputHandler(currentIndex, []);
          setIndex(currentIndex + 2);
        }
        break;

      case 16:
        // Question 10B
        if (usersResponse === 0) {
          // Open case with police
        }
        outputHandler(currentIndex, []);
        setIndex(currentIndex + 1);
        break;

      case 17:
        // Question 12B
        if (usersResponse === 0) {
          outputHandler(currentIndex, []);
        } else {
          outputHandler(currentIndex, []);
        }
        setIndex(currentIndex + 1);
        break;

      case 18:
        if (usersResponse === 0) {
          // Resources, then Q12
          outputHandler(currentIndex, ["undocumentedImmigrants"]);
        } else {
          outputHandler(currentIndex, []);
        }
        setIndex(currentIndex + 1);
        break;

      case 19:
        // Question 12
        if (usersResponse === 0) {
          // Resources, then Q12B
          outputHandler(currentIndex, ["trafickingSurvivor"]);
          setIndex(currentIndex + 1);
        } else {
          // Q13
          outputHandler(currentIndex, []);
          setIndex(currentIndex + 2);
        }
        break;

      case 20:
        if (usersResponse === 0) {
          // Resources, then Q13B
          outputHandler(currentIndex, ["legalAssistance"]);
          setIndex(currentIndex + 1);
        } else {
          // Q14
          outputHandler(currentIndex, []);
          setIndex(currentIndex + 3);
        }
        break;

      case 21:
        // Question 13B
        if (usersResponse === 0) {
          // Resources, then Q13C
          outputHandler(currentIndex, []);
          setIndex(currentIndex + 1);
        } else {
          // Q14
          outputHandler(currentIndex, []);
          setIndex(currentIndex + 2);
        }
        break;

      case 22:
        // Question 13C
        if (usersResponse === 0) {
          // Resources, then Q14
          outputHandler(currentIndex, []);
        } else {
          outputHandler(currentIndex, []);
        }
        setIndex(currentIndex + 1);
        break;

      case 23:
        // Question 14
        if (usersResponse === 0) {
          // Resources, then Q15
          outputHandler(currentIndex, ["homelessness"]);
          setIndex(currentIndex + 2);
        } else {
          // Q14B
          outputHandler(currentIndex, []);
          setIndex(currentIndex + 1);
        }
        break;

      case 24:
        // Question 14B
        if (usersResponse === 0) {
          // Resources, then Q15
          outputHandler(currentIndex, []);
        } else {
          outputHandler(currentIndex, []);
        }
        setIndex(currentIndex + 1);
        break;

      case 25:
        // Question 15
        if (usersResponse === 0) {
          // Resources, then Q16
          outputHandler(currentIndex, ["lgbtq+"]);
        } else {
          outputHandler(currentIndex, []);
        }
        setIndex(currentIndex + 1);
        break;

      case 26:
        // Question 16
        if (usersResponse === 0) {
          // Resources, then Q17
          outputHandler(currentIndex, ["relay"]);
        } else {
          outputHandler(currentIndex, []);
        }
        setIndex(currentIndex + 1);
        break;

      case 27:
        // Question 17
        if (usersResponse === 0) {
          // Resources, then form is completed
          outputHandler(currentIndex, []);
        } else {
          outputHandler(currentIndex, []);
        }
        setIndex(currentIndex + 1);
        break;
    }
  }, [outputHandler]);

  const goBackHandler = useCallback(() => {
    const prevIndex = outputRef.current.pop().index;
    setIndex(prevIndex);
  }, []);

  const onResponse = useCallback(
    (response) => {
      const currentIndex = indexRef.current;
      questionsRef.current[currentIndex].usersResponse = response;
      userResponses.push({ question: currentIndex, answer: response });

      // Update indexRef before questionManager reads it
      // (indexRef is already in sync via the effect, but we set it here
      // so questionManager can read the current index synchronously)
      questionManager();

      const numberOfQuestions = questionsRef.current.length;
      if (currentIndex === numberOfQuestions) {
        // questionnaire complete
      }
    },
    [questionManager]
  );

  const onLastQuestions = useCallback((response) => {
    let newIndex = response === 0 ? 17 : questions.length;
    setIndex(newIndex);
    setLastQuestionAsked(true);
  }, []);

  const selectedLocationHandler = useCallback((state) => {
    setLocationState(state);
  }, []);

  const length = questionsRef.current.length;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background:
          "linear-gradient(180deg, #FAF5FF 0%, #F3E5F5 40%, #EDE7F6 100%)",
      }}
    >
      {/* Sticky progress bar */}
      <LinearProgressWithLabel
        variant="determinate"
        value={(index / length) * 100}
      />

      {/* Back button */}
      {!(index === 1 || length === index) && (
        <Box
          sx={{
            px: { xs: 2, sm: 4 },
            pt: 2,
          }}
        >
          <Button
            onClick={goBackHandler}
            startIcon={<ChevronLeftIcon />}
            sx={{
              color: "text.secondary",
              fontWeight: 500,
              fontSize: "0.9rem",
              textTransform: "none",
              borderRadius: 2,
              px: 2,
              py: 0.75,
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(123, 31, 162, 0.08)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                border: "1px solid rgba(123, 31, 162, 0.15)",
                color: "primary.main",
                transform: "none",
                boxShadow: "0 2px 8px rgba(123, 31, 162, 0.08)",
              },
              transition: "all 0.2s ease",
            }}
          >
            Back
          </Button>
        </Box>
      )}

      {/* Main question area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, sm: 3 },
          py: { xs: 4, sm: 6 },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 700,
            backgroundColor: "rgba(255, 255, 255, 0.65)",
            backdropFilter: "blur(20px)",
            borderRadius: 4,
            border: "1px solid rgba(123, 31, 162, 0.06)",
            boxShadow: "0 8px 32px rgba(123, 31, 162, 0.08)",
            px: { xs: 2.5, sm: 5 },
            py: { xs: 4, sm: 5 },
          }}
        >
          <AnimatePresence mode="wait">
            {index === 18 && lastQuestionAsked === false ? (
              <SlidingQuestion
                key="last-question"
                questionObject={lastQuestionObject}
                buttonClick={onLastQuestions}
              />
            ) : index === 1 ? (
              <LocationDropdown
                key="location"
                questionObject={questionsRef.current[index]}
                locationState={locationState}
                locationHandler={selectedLocationHandler}
                buttonClick={onResponse}
              />
            ) : length > index ? (
              <SlidingQuestion
                key={`question-${index}`}
                questionObject={questionsRef.current[index]}
                buttonClick={onResponse}
              />
            ) : length === index ? (
              <motion.div
                key="completion"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{
                  duration: 0.4,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    py: { xs: 2, sm: 4 },
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg, rgba(123, 31, 162, 0.1) 0%, rgba(224, 64, 251, 0.1) 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                    }}
                  >
                    <CheckCircleOutlineIcon
                      sx={{
                        fontSize: 36,
                        color: "primary.main",
                      }}
                    />
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      lineHeight: 1.5,
                      mb: 1.5,
                      fontSize: {
                        xs: "1.15rem",
                        sm: "1.35rem",
                        md: "1.5rem",
                      },
                    }}
                  >
                    You have finished the questionnaire!
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.secondary",
                      mb: 4,
                      maxWidth: 460,
                      lineHeight: 1.6,
                    }}
                  >
                    Click below to view your curated list of resources,
                    personalized based on your responses.
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    component={Link}
                    to={{
                      pathname: "/resources",
                      resources: outputRef.current,
                    }}
                    state={{ resources: outputRef.current }}
                    sx={{
                      py: 1.5,
                      px: 5,
                      fontSize: "1.05rem",
                      borderRadius: 3,
                      background:
                        "linear-gradient(135deg, #7B1FA2 0%, #9C27B0 50%, #E040FB 100%)",
                      color: "#FFFFFF",
                      fontWeight: 600,
                      textTransform: "none",
                      boxShadow:
                        "0 4px 20px rgba(123, 31, 162, 0.3)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #6A1B9A 0%, #8E24AA 50%, #D500F9 100%)",
                        transform: "translateY(-2px)",
                        boxShadow:
                          "0 8px 32px rgba(123, 31, 162, 0.4)",
                      },
                      "&:active": {
                        transform: "translateY(0)",
                      },
                      transition:
                        "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    View Your Resources
                  </Button>
                </Box>
              </motion.div>
            ) : (
              console.log("unknown position reached", index)
            )}
          </AnimatePresence>
        </Box>
      </Box>
    </Box>
  );
};

export default Questionaire;
